import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { reload, loadPage, reset as resetPage } from "./reportsSlice";
import {
  setActiveReport,
  deleteRecord,
  loadRecord,
  reset,
} from "../report/reportSlice";
import { getSettingsData } from "../../api/server";
import ReportBuilder from "../report/components/ReportBuilder";
import { Modal, Tooltip, Button } from "antd";
import ActionsTable from "../../components/templates/ActionsTable";
import ReportCategories from "../../components/tables/ReportCategories";
import ReportLevel from "../../components/tables/ReportLevel";
import ReportModal from "../../components/templates/ReportModal";
import Surface from "../../components/layouts/Surface";
import Date from "../../components/tables/Date";
import { ReactComponent as BranchesImg } from "../../assets/no-branches.svg";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  TableOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import RestrictedComponent from "../../components/util/RestrictedComponent";
import Report from "../report";
const Reports = () => {
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const [visible, setVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportVisible, setReportVisible] = useState(false);
  const [reportMode, setReportMode] = useState(null);

  const { page: reportsPage } = useSelector((state) => state.reports);
  const { current, pageSize, total, order, items, filter } = reportsPage;
  const loading = useSelector(
    (state) => state.pendingActions["reports/loadPage"]
  );
  const activeReport = useSelector((state) => state.report.activeReport);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const intl = useIntl();

  useEffect(() => {
    getSettingsData({ name: "system_tables/report_categories" }).then(
      ({ error, data }) => {
        if (data) {
          setCategories(data.items);
        }
      }
    );
  }, []);

  const onTextChange = (e) => {
    dispatch(
      loadPage({ current: 1, filter: { ...filter, freeText: e.target.value } })
    );
  };

  const onShowReport = (record) => {
    // const url = location.pathname + "/" + record.id;
    // history.push(url);
    setReportVisible(true);
    dispatch(setActiveReport({ record }));
  };

  const onHide = () => {
    setReportVisible(false);
    // dispatch(setActiveReport(null));
  };

  const onDeleteReport = (record) => {
    const title = intl.formatMessage({
      id: "app.reports.deleteReportTitle",
    });
    const content = intl.formatMessage({
      id: "app.reports.deleteReportContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirmDelete(record);
      },
      onCancel() {},
    });
  };

  const onConfirmDelete = async (record) => {
    const action = await dispatch(deleteRecord(record.id));
    if (!action.error) {
      dispatch(
        loadPage({
          current: 1,
        })
      );
    }
  };

  const onEditReport = async (record) => {
    await dispatch(loadRecord({ scopePrefix, id: record.id }));
    setVisible(true);
  };

  const onDuplicateReport = async (record) => {
    await dispatch(loadRecord({ scopePrefix, id: record.id }));
    setReportMode("duplicate");
    setVisible(true);
  };

  const showReportBuilder = () => {
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
    setReportMode(null);
    dispatch(reset());
  };

  const chartTypes = [
    { name: "line", title: "Line", icon: <LineChartOutlined /> },
    { name: "area", title: "Area", icon: <AreaChartOutlined /> },
    { name: "bar", title: "Bar", icon: <BarChartOutlined /> },
    { name: "pie", title: "Pie", icon: <PieChartOutlined /> },
    { name: "table", title: "Table", icon: <TableOutlined /> },
    { name: "number", title: "Number", icon: <InfoCircleOutlined /> },
  ];

  return (
    <Surface>
      {contextHolder}
      <ActionsTable
        tableItems={[
          {
            title: "name",
            responsive: ["xl"],
            sortIndex: "name",
            sorter: true,
          },
          {
            title: "categories",
            responsive: ["xl"],
            ellipsis: true,
            render: ReportCategories,
            filters: categories
              .map((item, i) => ({ text: item.name, value: +item.id }))
              .sort((a, b) => a.text.localeCompare(b.text)),
          },
          {
            title: "show_in_branch",
            responsive: ["xl"],
            ellipsis: true,
            render: ReportLevel,
          },
          {
            title: "support_date_range",
            responsive: ["xl"],
            ellipsis: true,
            render: ({ record }) =>
              record.support_date_range ? <CheckCircleOutlined /> : null,
          },
          {
            title: "chartType",
            responsive: ["xl"],
            render: ({ record }) => {
              const icon = chartTypes.filter(
                (i) => i.name === record.chartType
              )[0].icon;
              return icon;
            },
          },
          {
            title: "updated_at",
            responsive: ["md"],
            render(text) {
              return (
                <Date text={text.record["updated_at"]} format={"DD/MM/YYYY"} />
              );
            },
            ellipsis: true,
            sortIndex: "updated_at",
            sorter: true,
          },
          {
            title: "actions",
            responsive: ["md"],
            width: "100px",
            render: ({ record }) => (
              <div style={{ display: "flex" }}>
                <Tooltip
                  title={intl.formatMessage({
                    id: "app.reports.showReport",
                  })}
                >
                  <Button
                    type="text"
                    icon={<SearchOutlined />}
                    onClick={() => onShowReport(record)}
                  />
                </Tooltip>
                {!record.built_in && (
                  <RestrictedComponent
                    resource={"reports"}
                    action={"editor"}
                    comp={null}
                  >
                    <Tooltip
                      title={intl.formatMessage({
                        id: "app.reports.editReport",
                      })}
                    >
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => onEditReport(record)}
                      />
                    </Tooltip>
                  </RestrictedComponent>
                )}

                <RestrictedComponent
                  resource={"reports"}
                  action={"editor"}
                  comp={null}
                >
                  <Tooltip
                    title={intl.formatMessage({
                      id: "app.reports.deplicateReport",
                    })}
                  >
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => onDuplicateReport(record)}
                    />
                  </Tooltip>
                </RestrictedComponent>
                {!record.built_in && (
                  <RestrictedComponent
                    resource={"reports"}
                    action={"delete"}
                    comp={null}
                  >
                    <Tooltip
                      title={intl.formatMessage({
                        id: "app.reports.deleteReport",
                      })}
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => onDeleteReport(record)}
                      />
                    </Tooltip>
                  </RestrictedComponent>
                )}
              </div>
            ),
          },
        ]}
        ltrItems={["phone_number", "fax_number"]}
        category={"reports"}
        resource="reports"
        current={current}
        pageSize={pageSize}
        total={total}
        order={order}
        loading={loading}
        textFilter={filter.freeText}
        reload={reload}
        loadPage={loadPage}
        resetPage={resetPage}
        items={items}
        filtersType={{ categories: "number" }}
        onTextChange={onTextChange}
        onAddClick={showReportBuilder}
        addbuttonText={<FormattedMessage id="app.reports.addNewReport" />}
        headerInputPlaceholder={intl.formatMessage({
          id: "app.reports.typeReportName",
        })}
        showheader={true}
        linkType={"branches"}
        emptyImage={<BranchesImg />}
        emptytext={<FormattedMessage id="app.tables.noBranches" />}
        autoFocus={true}
      />
      <Modal
        width={"100vw"}
        style={{
          height: "100vh",
          maxWidth: "100vw",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        styles={{
          body: {
            height: "100vh",
            width: "100%",
          },
        }}
        className={"no-padding no-border-radius"}
        closable={false}
        open={visible}
        footer={null}
        destroyOnClose={true}
      >
        <ReportBuilder
          activeReport={activeReport}
          closeModal={hide}
          reportMode={reportMode}
        />
      </Modal>

      <ReportModal visible={reportVisible} onHide={onHide}>
        {activeReport && <Report />}
      </ReportModal>
    </Surface>
  );
};

export default Reports;
